import { useEffect, useState } from 'react'

import type { QueueFilters } from '../api/queries'

const STORAGE_KEY = 'sentry.saved-views.v1'

interface SavedView {
  name: string
  filters: QueueFilters
}

function read(): SavedView[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? (parsed as SavedView[]) : []
  } catch {
    // A corrupt entry is discarded rather than allowed to break the queue. Losing a
    // saved filter is an inconvenience; a queue that will not render is not.
    return []
  }
}

/**
 * Saved filter sets.
 *
 * Stored in the browser in v1, and the label says so — a view that silently lives on
 * one laptop, while looking like something a team shares, is a promise the product
 * has not made good on. Server-side views are a small API away when they are asked
 * for.
 */
export function SavedViews({
  filters,
  onApply,
}: {
  filters: QueueFilters
  onApply: (filters: QueueFilters) => void
}) {
  const [views, setViews] = useState<SavedView[]>([])
  const [name, setName] = useState('')

  useEffect(() => setViews(read()), [])

  function persist(next: SavedView[]) {
    setViews(next)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  }

  const hasFilters = Object.values(filters).some((value) => value !== undefined && value !== '')

  return (
    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
      {views.map((view) => (
        <span key={view.name} className="badge">
          <button
            style={{ background: 'none', border: 0, padding: 0 }}
            onClick={() => onApply(view.filters)}
          >
            {view.name}
          </button>
          <button
            aria-label={`Delete the saved view ${view.name}`}
            style={{ background: 'none', border: 0, padding: 0 }}
            onClick={() => persist(views.filter((item) => item.name !== view.name))}
          >
            ×
          </button>
        </span>
      ))}

      <input
        aria-label="Name for this view"
        placeholder="save this view as…"
        value={name}
        onChange={(event) => setName(event.target.value)}
        style={{ width: 160 }}
      />
      <button
        disabled={!name.trim() || !hasFilters}
        onClick={() => {
          persist([
            ...views.filter((item) => item.name !== name.trim()),
            { name: name.trim(), filters },
          ])
          setName('')
        }}
      >
        Save view
      </button>
      <span className="muted" style={{ fontSize: 'var(--text-xs)' }}>
        saved in this browser only
      </span>
    </div>
  )
}
